"use client"; // Indicate this is a client component

import { SignInButton, useUser } from "@clerk/clerk-react"; // Clerk useUser und SignInButton importieren
import { api } from "@convex/_generated/api"; // Use alias
import { useMutation, useQuery } from "convex/react";
import { useEffect, useState } from "react";
// import { v4 as uuidv4 } from "uuid"; // nutzerHash wird nicht mehr benötigt
import { useLocation, useParams } from "wouter";
import PageLayout from "@/components/_shared/PageLayout";
import QRCodeModal from "@/components/_shared/QRCodeModal";
import Toast from "@/components/_shared/Toast";
import { FileList } from "@/components/file-share/FileList";
import { FileUploader } from "@/components/file-share/FileUploader";
import { RoomPasswordModal } from "@/components/file-share/RoomPasswordModal";
import { RoomSettingsModal } from "@/components/file-share/RoomSettingsModal";
import { RoomStatusLeiste } from "@/components/file-share/RoomStatusLeiste";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { formatBytes } from "@/lib/utils";

export default function FileShareRoomPage() {
	const { user } = useUser(); // Aktuellen Clerk-Benutzer laden
	const [, navigate] = useLocation();
	const params = useParams<{ roomId?: string }>();
	const raumId = params?.roomId || "";

	// State
	// const [nutzerHash, setNutzerHash] = useState<string>(""); // nutzerHash wird nicht mehr benötigt
	const [ladeVorgang, setLadeVorgang] = useState(true);
	const [erfolgsMeldung, setErfolgsMeldung] = useState<string | null>(null);
	const [fehlerMeldung, setFehlerMeldung] = useState<string | null>(null);
	const [aufrufGezaehlt, setAufrufGezaehlt] = useState(false);
	const [istBesitzer, setIstBesitzer] = useState(false);
	const [ablaufDialogAnzeigen, setAblaufDialogAnzeigen] = useState(false);
	const [passwortDialogAnzeigen, setPasswortDialogAnzeigen] = useState(false);
	const [settingsModalOpen, setSettingsModalOpen] = useState(false);
	const [qrModalOpen, setQrModalOpen] = useState(false);
	const [qrLink, setQrLink] = useState("");
	const [passwortGeprueft, setPasswortGeprueft] = useState(false);
	const [passwortFehler, setPasswortFehler] = useState<string | null>(null);
	const [passwortPruefungLaeuft, setPasswortPruefungLaeuft] = useState(false);

	// Convex Queries & Mutations
	const raum = useQuery(api.fileShare.raumLaden, raumId ? { raumId } : "skip");
	const dateien = useQuery(
		api.fileShare.dateienLaden,
		raum && passwortGeprueft && raumId ? { raumId } : "skip",
	);
	// Ablaufzeit zurücksetzen entfernt
	const aufrufZaehlenMutation = useMutation(api.fileShare.aufrufZaehlen);
	const passwortPruefenMutation = useMutation(api.fileShare.passwortPruefen);

	// Load/create user hash - nicht mehr benötigt, da Clerk verwendet wird
	// useEffect(() => {
	// const gespeicherterHash = localStorage.getItem("fileShare_nutzerHash");
	// if (gespeicherterHash) {
	// setNutzerHash(gespeicherterHash);
	// } else {
	// const neuerHash = uuidv4();
	// localStorage.setItem("fileShare_nutzerHash", neuerHash);
	// setNutzerHash(neuerHash);
	// }
	// }, []);

	// Handle initial room load and password check
	useEffect(() => {
		if (raum !== undefined) {
			setLadeVorgang(false);
			if (raum === null) {
				setAblaufDialogAnzeigen(true);
			} else {
				// Logik für istBesitzer an Clerk anpassen
				const isOwner =
					!!user && !!raum.clerkUserId && user.id === raum.clerkUserId;
				setIstBesitzer(isOwner);
				if (raum.passwort && !passwortGeprueft) {
					setPasswortDialogAnzeigen(true);
				} else {
					setPasswortGeprueft(true); // Access granted (no pwd or already checked)
				}
			}
		}
	}, [raum, user, passwortGeprueft]); // nutzerHash aus Abhängigkeiten entfernt, user hinzugefügt

	// Count view after access granted
	useEffect(() => {
		if (raum && passwortGeprueft && !aufrufGezaehlt && raumId) {
			aufrufZaehlenMutation({ raumId }).catch(() => {});
			setAufrufGezaehlt(true);
		}
	}, [raum, passwortGeprueft, raumId, aufrufZaehlenMutation, aufrufGezaehlt]);

	// Auto-hide toasts
	useEffect(() => {
		let timer: NodeJS.Timeout | null = null;
		if (erfolgsMeldung) {
			timer = setTimeout(() => setErfolgsMeldung(null), 3000);
		}
		return () => {
			if (timer) clearTimeout(timer);
		};
	}, [erfolgsMeldung]);

	useEffect(() => {
		let timer: NodeJS.Timeout | null = null;
		if (fehlerMeldung) {
			timer = setTimeout(() => setFehlerMeldung(null), 5000);
		}
		return () => {
			if (timer) clearTimeout(timer);
		};
	}, [fehlerMeldung]);

	// Handle password submission
	const handlePasswortPruefen = async (passwortInput: string) => {
		if (!(raumId && passwortInput)) return;
		setPasswortFehler(null);
		setPasswortPruefungLaeuft(true);
		try {
			const result = await passwortPruefenMutation({
				raumId,
				passwort: passwortInput,
			});
			if (result.erfolg) {
				setPasswortGeprueft(true);
				setPasswortDialogAnzeigen(false);
				setErfolgsMeldung("Zugriff gewährt!");
			} else {
				setPasswortFehler(result.grund || "Falsches Passwort.");
			}
		} catch (_error) {
			setPasswortFehler("Fehler bei der Passwortprüfung.");
		} finally {
			setPasswortPruefungLaeuft(false);
		}
	};

	// Copy room link
	const handleLinkKopieren = () => {
		const url = window.location.href;
		navigator.clipboard.writeText(url).then(
			() => setErfolgsMeldung("Link kopiert!"),
			() => setFehlerMeldung("Kopieren fehlgeschlagen."),
		);
	};

	// Show QR code
	const handleQrAnzeigen = () => {
		setQrLink(window.location.href);
		setQrModalOpen(true);
	};

	// Navigate back if room expired/not found
	const handleAblaufRoom = () => {
		setAblaufDialogAnzeigen(false);
		navigate("/file");
	};

	// Navigate back if password modal closed without success
	const handlePasswordModalClose = () => {
		setPasswortDialogAnzeigen(false);
		if (!passwortGeprueft) {
			navigate("/file");
		}
	};

	// Permissions derived from room state
	const darfDateienHochladen =
		raum &&
		passwortGeprueft &&
		(raum.uploadModus === "anonym" ||
			(raum.uploadModus === "eingeloggt" && !!user) ||
			(raum.uploadModus === "besitzer" && istBesitzer));
	const nurBesitzerDarfLoeschen = raum?.nurBesitzerLoeschen ?? true;

	// Bedingung für die Anzeige einer "Bitte anmelden"-Meldung
	const zeigeAnmeldeAufforderungFuerUpload =
		!user && raum && passwortGeprueft && raum.uploadModus === "eingeloggt";

	// Storage calculation
	const maxSpeicher = 100 * 1024 * 1024; // 100MB
	const genutzterSpeicher = raum?.gesamtGroesse ?? 0;
	const verbleibenderSpeicher = Math.max(0, maxSpeicher - genutzterSpeicher);
	const speicherProzent = raum
		? Math.min(100, (genutzterSpeicher / maxSpeicher) * 100)
		: 0;

	// Settings button for owner
	const headerButton = null;

	// Loading state determination
	const showLoadingSpinner =
		ladeVorgang || (raum?.passwort && !passwortGeprueft);

	// Set document title
	useEffect(() => {
		document.title = raumId ? `FileShare Raum: ${raumId}` : "FileShare Raum";
	}, [raumId]);

	return (
		<PageLayout
			backUrl="/file"
			extraHeaderButton={headerButton}
			title="FileShare"
			// hideDelete={true} // Prop entfernt, da der Button im PageLayout entfernt wurde
			toolId="fileshare"
		>
			<Toast
				message={erfolgsMeldung || ""}
				onClose={() => setErfolgsMeldung(null)}
				visible={!!erfolgsMeldung}
			/>
			<Toast
				message={fehlerMeldung || ""}
				onClose={() => setFehlerMeldung(null)}
				type="error"
				visible={!!fehlerMeldung}
			/>

			<div className="space-y-4">
				{showLoadingSpinner &&
				!ablaufDialogAnzeigen &&
				!passwortDialogAnzeigen ? (
					<div className="flex items-center justify-center py-20">
						<div className="h-16 w-16 animate-spin rounded-full border-4 border-blue-200 border-t-blue-500 dark:border-gray-600 dark:border-t-blue-400" />
					</div>
				) : raum && passwortGeprueft ? (
					<>
						<RoomStatusLeiste
							istBesitzer={istBesitzer}
							nurBesitzerLoeschen={raum.nurBesitzerLoeschen}
							nurBesitzerUpload={
								raum.uploadModus !== "anonym" &&
								raum.uploadModus !== "eingeloggt"
							}
							onLinkKopieren={handleLinkKopieren}
							onQrAnzeigen={handleQrAnzeigen}
							onOpenSettings={() => setSettingsModalOpen(true)}
							onVerlaengernClick={() => {}}
							passwort={raum.passwort ? "geschützt" : null}
							raumId={raumId}
						/>

						{/* Storage Indicator */}
						<div className="rounded-lg border border-gray-100 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800/70">
							<div className="flex w-full flex-col">
								<div className="mb-2 flex justify-between text-sm">
									<span className="font-medium text-gray-600 dark:text-gray-300">
										Speicherplatz
									</span>
									<span className="text-gray-500 dark:text-gray-400">
										{formatBytes(genutzterSpeicher)} /{" "}
										{formatBytes(maxSpeicher)}
									</span>
								</div>
								<div className="h-2.5 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
									<div
										className={`h-full transition-all duration-300 ${
											speicherProzent >= 95
												? "bg-red-500"
												: speicherProzent >= 75
													? "bg-amber-500"
													: "bg-blue-500"
										}`}
										style={{ width: `${speicherProzent}%` }}
									/>
								</div>
							</div>
						</div>

						{zeigeAnmeldeAufforderungFuerUpload && (
							<div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4 text-center dark:border-yellow-700/50 dark:bg-yellow-900/30">
								<p className="font-medium text-yellow-700 dark:text-yellow-300">
									Dieser Raum erlaubt Uploads von allen Nutzern.
								</p>
								<p className="mt-1 text-sm text-yellow-600 dark:text-yellow-400">
									Bitte melden Sie sich an, um Dateien hochzuladen.
								</p>
								<SignInButton mode="modal">
									<Button className="mt-3" variant="outline">
										Anmelden / Registrieren
									</Button>
								</SignInButton>
							</div>
						)}

						{darfDateienHochladen && (
							<FileUploader
								maxSize={verbleibenderSpeicher}
								// nutzerHash={nutzerHash} // nutzerHash Prop entfernt
								onError={setFehlerMeldung}
								onSuccess={setErfolgsMeldung}
								raumId={raumId}
							/>
						)}

						<FileList
							dateien={dateien === undefined ? [] : dateien || []} // Vergleich auf undefined statt "skip"
							// nutzerHash={nutzerHash} // nutzerHash Prop entfernt
							istBesitzer={istBesitzer}
							nurBesitzerLoeschen={nurBesitzerDarfLoeschen}
							onError={setFehlerMeldung}
							onSuccess={setErfolgsMeldung}
						/>
					</>
				) : null}
			</div>

			{/* Modals and Dialogs */}
			<AlertDialog
				onOpenChange={(open) => !open && handleAblaufRoom()}
				open={ablaufDialogAnzeigen}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>Raum nicht verfügbar</AlertDialogTitle>
						<AlertDialogDescription>
							Dieser FileShare-Raum existiert nicht oder ist bereits abgelaufen.
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogAction onClick={handleAblaufRoom}>
							Zurück zur Übersicht
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>

			<RoomPasswordModal // Wird jetzt für den initialen Passwort-Zugriff verwendet
				error={passwortFehler}
				isLoading={passwortPruefungLaeuft}
				isOpen={passwortDialogAnzeigen} // Direkter Aufruf von handlePasswortPruefen
				onClose={handlePasswordModalClose} // Spezifischen Passwortfehler verwenden
				onSubmit={handlePasswortPruefen} // Korrekten Ladezustand verwenden
			/>

			{raum &&
				istBesitzer && ( // SettingsModal nur für Besitzer und wenn Raumdetails geladen
					<RoomSettingsModal
						initialSettings={{
							// Diese States müssen noch definiert werden, falls sie für die UI benötigt werden
							// oder direkt aus `raum` gelesen werden, falls das Modal die Daten selbst holt/aktualisiert.
							// Vorerst auskommentiert, da die States `passwort`, `nurBesitzerUpload`, `nurBesitzerLoeschen` nicht existieren.
							// passwort: passwort,
							// nurBesitzerUpload: nurBesitzerUpload,
							// nurBesitzerLoeschen: nurBesitzerLoeschen,
							// Stattdessen können wir die Werte direkt aus dem `raum`-Objekt nehmen, wenn das Modal sie braucht
							passwort: raum?.passwort ? "******" : "", // Beispiel: Passwort nicht direkt anzeigen
							nurBesitzerUpload: raum?.nurBesitzerUpload ?? false,
							nurBesitzerLoeschen: raum?.nurBesitzerLoeschen ?? false,
						}}
						isOpen={settingsModalOpen}
						onClose={() => setSettingsModalOpen(false)}
						// nutzerHash Prop wird in RoomSettingsModal nicht mehr benötigt und wurde dort entfernt
						onError={setFehlerMeldung}
						// onPasswortChange, onNurBesitzerUploadChange, onNurBesitzerLoeschenChange sind vermutlich nicht mehr nötig,
						// wenn das Modal die Änderungen direkt per Mutation speichert.
						// onPasswortChange={setPasswort}
						// onNurBesitzerUploadChange={setNurBesitzerUpload}
						// onNurBesitzerLoeschenChange={setNurBesitzerLoeschen}
						// onSave={handleEinstellungenAktualisieren} // Diese Funktion muss ggf. angepasst werden oder das Modal macht es selbst
						onSuccess={setErfolgsMeldung}
						raumId={raumId}
					/>
				)}

			<QRCodeModal
				isOpen={qrModalOpen}
				link={qrLink}
				onClose={() => setQrModalOpen(false)}
				title="QR-Code für FileShare-Raum"
			/>
		</PageLayout>
	);
}
